import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { Controller, useForm } from "react-hook-form";

import { ApiLogEntry, fetchApiLogs } from "@/apis/log";
import Button from "@/components/Button";
import Pagination from "@/components/Pagination";
import Table from "@/components/Table";
import DatePickerButton from "@/pages/Reservation/components/DropdownDatePicker";
import type { CellInfo } from "@/types/table";
import { getTableHeader } from "@/utils/table";

const methodColors: Record<string, string> = {
  GET: "bg-blue-100 text-blue-700",
  POST: "bg-green-100 text-green-700",
  PUT: "bg-yellow-100 text-yellow-700",
  DELETE: "bg-red-100 text-red-700",
};

const today = dayjs();

// 공용 Table 사용: 데스크톱은 표, 모바일(md 미만)은 카드 목록으로 렌더링된다
const logHeader = getTableHeader(
  ["시간", "메서드", "URL", "상태", "사용자", "역할", "IP", "응답시간"],
  ["13%", "9%", "29%", "8%", "13%", "9%", "11%", "8%"],
);

const LogManagement = () => {
  const { control, getValues } = useForm({
    defaultValues: {
      fromDate: today.subtract(7, "day"),
      toDate: today,
      userId: "",
    },
  });

  const [logs, setLogs] = useState<ApiLogEntry[]>([]);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [userIdFilter, setUserIdFilter] = useState("");

  const getLogs = async () => {
    const fromDate = getValues("fromDate").format("YYYYMMDD");
    const toDate = getValues("toDate").format("YYYYMMDD");

    const res = await fetchApiLogs(page, 20, {
      userId: userIdFilter || undefined,
      fromDate,
      toDate,
    });
    setLogs(res.content);
    setPageCount(res.totalPages);
    setTotalCount(res.totalElements);
  };

  useEffect(() => {
    getLogs();
  }, [page]);

  const handleSearch = () => {
    setPage(0);
    getLogs();
  };

  const tableBody: CellInfo[][] = logs.map(log => [
    { name: "시간", data: dayjs(log.crDt).format("MM-DD HH:mm:ss") },
    {
      name: "메서드",
      data: (
        <span
          className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${methodColors[log.method] || "bg-gray-100 text-gray-700"}`}
        >
          {log.method}
        </span>
      ),
    },
    {
      name: "URL",
      data: (
        <span className="break-all font-mono text-xs" title={log.url}>
          {log.url}
        </span>
      ),
    },
    {
      name: "상태",
      data: (
        <span
          className={
            log.statusCode && log.statusCode >= 400
              ? "font-medium text-red-500"
              : "text-green-600"
          }
        >
          {log.statusCode ?? "-"}
        </span>
      ),
    },
    { name: "사용자", data: log.userName || log.userId || "-" },
    { name: "역할", data: log.roleId || "-" },
    {
      name: "IP",
      data: <span className="font-mono text-xs">{log.ipAddress || "-"}</span>,
    },
    {
      name: "응답시간",
      data: log.responseTime !== null ? `${log.responseTime}ms` : "-",
    },
  ]);

  return (
    <div className="flex w-full flex-col gap-4 p-4 text-base text-black">
      <div className="flex flex-1 flex-row flex-wrap items-center gap-2">
        <div className="w-40">
          <Controller
            name="fromDate"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DatePickerButton date={value} onChange={onChange} />
            )}
          />
        </div>
        <span className="text-gray-500">~</span>
        <div className="w-40">
          <Controller
            name="toDate"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DatePickerButton
                date={value}
                startDate={value}
                onChange={onChange}
              />
            )}
          />
        </div>
        <input
          type="text"
          placeholder="사용자 ID"
          value={userIdFilter}
          onChange={e => setUserIdFilter(e.target.value)}
          className="h-9 w-40 rounded border border-gray-300 px-3 text-small"
        />
        <Button variant="outlined" onClick={handleSearch}>
          조회
        </Button>
        <span className="ml-auto text-small text-gray-500">
          총 {totalCount.toLocaleString()}건
        </span>
      </div>

      <div className="flex flex-col items-center gap-6 bg-white px-3 py-4">
        {logs.length === 0 ? (
          <div className="w-full rounded-lg border border-gray-200 bg-white py-10 text-center text-sm text-gray-400">
            로그가 없습니다.
          </div>
        ) : (
          <Table header={logHeader} body={tableBody} />
        )}
        {pageCount > 0 && (
          <Pagination count={pageCount} page={page} onChange={setPage} />
        )}
      </div>
    </div>
  );
};

export default LogManagement;
